import { api, unwrap } from '@/api'
import {
  storeTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
} from '@/lib/api-client'
import type {
  ChangePasswordInput,
  ForgotPasswordInput,
  LoginInput,
  LoginResponse,
  MeResponse,
  RegisterInput,
  ResetPasswordInput,
  UpdateProfileInput,
  User,
  VerifyEmailInput,
} from '../types'

export const authApi = {
  async register(input: RegisterInput): Promise<User> {
    return unwrap<User>(await api.post<User>('/auth/register', input))
  },

  async login(input: LoginInput): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', input)
    const data = unwrap<LoginResponse>(response)
    storeTokens(data.accessToken, data.refreshToken)
    return data
  },

  async refresh(): Promise<string | null> {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      finalizeLogout()
      return null
    }
    try {
      const response = await api.post<{ accessToken: string; refreshToken: string }>(
        '/auth/refresh',
        { refreshToken },
      )
      const { accessToken, refreshToken: nextRefreshToken } = unwrap(response)
      storeTokens(accessToken, nextRefreshToken)
      return accessToken
    } catch {
      finalizeLogout()
      return null
    }
  },

  async logout(): Promise<void> {
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      try {
        await api.post<void>('/auth/logout', { refreshToken })
      } catch {
        // best-effort server cleanup; local cleanup always runs
      }
    }
    finalizeLogout()
  },

  async me(): Promise<MeResponse> {
    return unwrap<MeResponse>(await api.get<MeResponse>('/auth/me'))
  },

  async updateProfile(input: UpdateProfileInput): Promise<User> {
    return unwrap<User>(await api.patch<User>('/auth/profile', input))
  },

  async changePassword(input: ChangePasswordInput): Promise<void> {
    await api.post<void>('/auth/change-password', input)
  },

  async forgotPassword(input: ForgotPasswordInput): Promise<void> {
    await api.post<void>('/auth/forgot-password', input)
  },

  async resetPassword(input: ResetPasswordInput): Promise<void> {
    await api.post<void>('/auth/reset-password', input)
  },

  async verifyEmail(input: VerifyEmailInput): Promise<void> {
    await api.post<void>('/auth/verify-email', input)
  },

  async resendVerification(email: string): Promise<void> {
    await api.post<void>('/auth/resend-verification', { email })
  },
}

export function finalizeLogout(): void {
  clearTokens()
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken())
}

export type { MeResponse, User }